'use client';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

import { MIXPANEL_EVENT_PREFIX } from '@/lib/actions/analytics/constants';
import { ssrTrack } from '@/lib/actions/analytics/ssr-track';
import { FullPageSpinner } from '@/ui/components/FullPageSpinner';
import { AppShell } from '@mantine/core';
import mixpanel from 'mixpanel-browser';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

export default function AppShellWrapper({ children }: React.PropsWithChildren) {
    const { data: session, status } = useSession();
    const pathname = usePathname();

    const email = session?.user?.email;
    const name = session?.user?.name;

    useEffect(() => {
        // Don't log search params in case there are sensitive data like auth
        // tokens in there.
        ssrTrack('navigate', { pathname });
    }, [pathname]);

    useEffect(() => {
        mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN!, {
            debug: process.env.NODE_ENV === 'development',
            // This doesn't work at all.
            // track_pageview: true,
            persistence: 'localStorage',
        });

        const stored = window.localStorage.getItem('signedIn');

        if (email && status === 'authenticated') {
            const isSunrunner = email.endsWith('@sunrun.com');

            try {
                mixpanel.identify(email);
            } catch (e) {
                console.error(`mixpanel.identify(email) error:`, e);
            }

            try {
                mixpanel.people.set({
                    $email: email,
                    name,
                    isSunrunner,
                    environment: process.env.NODE_ENV,
                });
            } catch (e) {
                console.error(`mixpanel.people.set error:`, e);
            }

            if (!stored) {
                window.localStorage.setItem('signedIn', '1');

                ssrTrack('signedIn');

                // Track a very limited number of events on client side to
                // capture default auto-populated properties for this user.
                mixpanel.track(MIXPANEL_EVENT_PREFIX + 'client signedIn');
            }
        } else {
            if (stored) {
                window.localStorage.removeItem('signedIn');

                ssrTrack('signedOut');

                // Track a very limited number of events on client side to
                // capture default auto-populated properties for this user.
                mixpanel.track(MIXPANEL_EVENT_PREFIX + 'client signedOut');
            }
        }
    }, [email, name, status]);

    if (status === 'loading') {
        return <FullPageSpinner />;
    }

    return <AppShell>{children}</AppShell>;
}
